/**
 * @fileoverview System report definition service — local-first CRUD for system-scoped report definitions.
 * @module services/systemReportDefinitionService
 *
 * Responsibilities:
 * - CRUD operations for system report definitions in Dexie.
 * - Publish/archive lifecycle management.
 * - Library toggle and tenant seeding (deprecated).
 * - Audit logging for all mutations.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb, wakeLocalWrite)
 * - packages/audit (runAuditedMutation)
 * - ./systemAuditLogService (writeAuditLog)
 * - ../apiClient (apiFetch — only for deprecated seedToTenant)
 *
 * Architecture:
 * - Local-first: Dexie is source of truth; background sync reconciles with MongoDB.
 *
 * @localFirst
 * Source of Truth: local (Dexie)
 * Write Flow: Dexie write → wakeLocalWrite → sync push to MongoDB
 * Sync Strategy: Optimistic write; background push
 * Conflict Resolution: Last-write-wins (server timestamp)
 *
 * Notes:
 * - publish/archive/toggleLibrary/duplicate delegate to updateReportDefinition or
 *   createReportDefinition and are audited through those paths.
 * - seedToTenant is deprecated; uses API directly (inherently-online exception).
 */

import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import type { ReportDefinitionLocal } from '@firesquirrel/local-db';
import { runAuditedMutation } from '../../../packages/audit/src';
import { writeAuditLog } from './systemAuditLogService';
import { apiFetch } from '../apiClient';

export interface ListReportDefinitionsParams {
  type?: 'report' | 'dashboard';
  status?: 'draft' | 'published' | 'archived';
  tenantId?: string;
  includeSystem?: boolean;
  includeDeleted?: boolean;
  scopeLevel?: 'system' | 'department';
}

export async function listReportDefinitions(
  params?: ListReportDefinitionsParams,
): Promise<ReportDefinitionLocal[]> {
  const db = await getSystemDb();
  const results = await db.report_definitions.toArray();
  let items = results as ReportDefinitionLocal[];

  if (!params?.includeDeleted) {
    items = items.filter((item) => !item._deleted);
  }

  if (params?.type) {
    items = items.filter((item) => item.type === params.type);
  }

  if (params?.status) {
    items = items.filter((item) => item.status === params.status);
  }

  if (params?.tenantId) {
    items = items.filter((item) => item.scope.tenantId === params.tenantId);
  }

  if (params?.scopeLevel === 'system') {
    items = items.filter((item) => item.scope?.level === 'system');
  }

  if (params?.scopeLevel === 'department') {
    items = items.filter((item) => item.scope?.level === 'department');
  }

  if (params?.includeSystem === false) {
    items = items.filter((item) => item.scope.tenantId);
  }

  return items.sort((a, b) => {
    const aDate = new Date(a.updatedAt || a.createdAt).getTime();
    const bDate = new Date(b.updatedAt || b.createdAt).getTime();
    return bDate - aDate;
  });
}

export async function getReportDefinition(id: string): Promise<ReportDefinitionLocal | null> {
  const db = await getSystemDb();
  const doc = await db.report_definitions.get(id);
  return (doc as ReportDefinitionLocal | undefined) ?? null;
}

/** @process createReportDefinition — Dexie mutation + audit entry. */
export async function createReportDefinition(
  data: Partial<ReportDefinitionLocal>,
): Promise<ReportDefinitionLocal> {
  const db = await getSystemDb();
  const now = new Date().toISOString();
  const newId = data._id || `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  return runAuditedMutation<null, ReportDefinitionLocal>({
    collection: 'systemReportDefinitions',
    documentId: newId,
    action: 'create',
    summary: `Created system report definition: ${data.name ?? 'Untitled'}`,
    getBefore: async () => null,
    execute: async () => {
      const newDoc = {
        ...data,
        _id: newId,
        createdAt: now,
        updatedAt: now,
        version: 1,
      };
      await db.report_definitions.put(newDoc as any);
      wakeLocalWrite('report_definitions');
      return newDoc as ReportDefinitionLocal;
    },
    getAfter: async () => (await db.report_definitions.get(newId)) as ReportDefinitionLocal | null,
    writeAuditLog,
  });
}

/** @process updateReportDefinition — Dexie mutation + audit entry. */
export async function updateReportDefinition(
  id: string,
  patch: Partial<ReportDefinitionLocal>,
): Promise<ReportDefinitionLocal | null> {
  const db = await getSystemDb();
  const existing = await db.report_definitions.get(id);
  if (!existing) return null;

  return runAuditedMutation<ReportDefinitionLocal, ReportDefinitionLocal>({
    collection: 'systemReportDefinitions',
    documentId: id,
    action: 'update',
    summary: `Updated system report definition: ${(existing as any).name ?? id}`,
    getBefore: async () => (await db.report_definitions.get(id)) as ReportDefinitionLocal | null,
    execute: async () => {
      const updated = { ...(existing as any), ...patch, updatedAt: new Date().toISOString() };
      await db.report_definitions.put(updated);
      wakeLocalWrite('report_definitions');
      return updated as ReportDefinitionLocal;
    },
    getAfter: async () => (await db.report_definitions.get(id)) as ReportDefinitionLocal | null,
    writeAuditLog,
  });
}

/** @process deleteReportDefinition — Dexie mutation + audit entry. */
export async function deleteReportDefinition(id: string): Promise<boolean> {
  const db = await getSystemDb();
  const existing = await db.report_definitions.get(id);
  if (!existing) return false;

  await runAuditedMutation<ReportDefinitionLocal, ReportDefinitionLocal>({
    collection: 'systemReportDefinitions',
    documentId: id,
    action: 'delete',
    summary: `Deleted system report definition: ${(existing as any).name ?? id}`,
    getBefore: async () => (await db.report_definitions.get(id)) as ReportDefinitionLocal | null,
    execute: async () => {
      const tombstone = { ...(existing as any), _deleted: true, deletedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      await db.report_definitions.put(tombstone);
      wakeLocalWrite('report_definitions');
      return tombstone as ReportDefinitionLocal;
    },
    getAfter: async () => (await db.report_definitions.get(id)) as ReportDefinitionLocal | null,
    writeAuditLog,
  });

  return true;
}

/** @process duplicateReportDefinition — delegates to audited mutation. */
export async function duplicateReportDefinition(id: string): Promise<ReportDefinitionLocal | null> {
  const original = await getReportDefinition(id);
  if (!original) return null;

  const copy = {
    ...original,
    _id: undefined,
    name: `${original.name} (Copy)`,
    status: 'draft' as const,
    isInLibrary: false,
    publishedAt: undefined,
    updatedAt: new Date().toISOString(),
  };

  return createReportDefinition(copy);
}

/** @process publishReportDefinition — delegates to audited mutation. */
export async function publishReportDefinition(id: string): Promise<ReportDefinitionLocal | null> {
  const existing = await getReportDefinition(id);
  if (!existing || existing.scope?.level !== 'system') return null;
  return updateReportDefinition(id, {
    status: 'published',
  });
}

/** @process archiveReportDefinition — delegates to audited mutation. */
export async function archiveReportDefinition(id: string): Promise<ReportDefinitionLocal | null> {
  const existing = await getReportDefinition(id);
  if (!existing || existing.scope?.level !== 'system') return null;
  return updateReportDefinition(id, {
    status: 'archived',
    isInLibrary: false,
  });
}

/** @process toggleLibrary — delegates to audited mutation. */
export async function toggleLibrary(
  id: string,
  isInLibrary: boolean,
): Promise<ReportDefinitionLocal | null> {
  const existing = await getReportDefinition(id);
  if (!existing || existing.scope?.level !== 'system' || existing.status !== 'published') return null;

  // Publish-readiness guard: ensure report has minimum viable config before library exposure
  if (isInLibrary) {
    const config = existing.reportConfig;
    const hasColumns = config?.columns && config.columns.length > 0;
    const hasDataSources = config?.dataSources && config.dataSources.length > 0;
    if (!hasColumns && !hasDataSources) {
      console.warn(`[toggleLibrary] Report ${id} cannot be added to library: missing columns and data sources`);
      return null;
    }
  }

  return updateReportDefinition(id, { isInLibrary });
}

/**
 * @deprecated Use the library-first adoption model instead. Tenants should adopt reports
 * via `systemLibraryAdoptionService.adoptSystemReportDefinition()` after reports are
 * published and added to the library. This seed-down approach is no longer the primary
 * delivery model and will be removed in a future release.
 */
export async function seedToTenant(
  reportId: string,
  tenantIds: string[],
): Promise<{ success: boolean; seededCount: number }> {
  console.warn('[seedToTenant] Deprecated: Use library-first adoption via adoptSystemReportDefinition() instead');
  try {
    const result = await apiFetch(`/api/system/report-definitions/${reportId}/seed-to-tenant`, {
      method: 'POST',
      body: JSON.stringify({ tenantIds }),
    });
    
    return { success: result.success, seededCount: result.seededCount };
  } catch (error) {
    console.error('[seedToTenant] Error:', error);
    return { success: false, seededCount: 0 };
  }
}
